import { useEffect, useRef } from "react";

/**
 * Integrates a modal/dialog/sheet/drawer with the browser back button.
 *
 * When `open` becomes true, a sentinel history entry is pushed so that
 * pressing the device/browser back button closes the modal without
 * leaving the current route. Closing via X / ESC / outside-click still
 * works and cleans up the pushed entry so history stays tidy.
 *
 * Stacked modals are supported via a LIFO module-level stack; back closes
 * the inner-most overlay first.
 */

type Entry = { id: string; close: () => void };

const stack: Entry[] = [];
let listenerAttached = false;
// True while we are processing a popstate event triggered by the user
// pressing back, OR a synthetic history.back() we issued ourselves.
let poppingFromUs = false;
// UI closes remove their own history entry, not the next overlay's entry.
let pendingCleanupPops = 0;

const popOwnedHistory = () => {
  pendingCleanupPops += 1;
  try {
    window.history.back();
  } catch {
    pendingCleanupPops -= 1;
  }
};

const ensureListener = () => {
  if (listenerAttached || typeof window === "undefined") return;
  listenerAttached = true;
  window.addEventListener("popstate", () => {
    if (pendingCleanupPops > 0) {
      pendingCleanupPops -= 1;
      return;
    }
    if (stack.length === 0) return;
    const top = stack[stack.length - 1];
    if (!top) return;
    poppingFromUs = true;
    try {
      top.close();
    } finally {
      // Reset on next macrotask once React has flushed the close.
      setTimeout(() => {
        poppingFromUs = false;
        // A guarded close may leave the dialog open. Restore its Back entry.
        if (stack.some((entry) => entry.id === top.id)) {
          try {
            window.history.pushState({ ...window.history.state, [SENTINEL_KEY]: top.id }, "");
          } catch {
            // History may be unavailable in embedded browsers.
          }
        }
      }, 0);
    }
  });
};

const SENTINEL_KEY = "lovableModal";

export function useBackToClose(open: boolean, onClose: () => void) {
  const idRef = useRef<string | null>(null);
  const ownsEntryRef = useRef(false);
  const closeRef = useRef(onClose);
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep latest close callback without re-running the open effect.
  useEffect(() => {
    closeRef.current = onClose;
    if (ownsEntryRef.current && idRef.current) {
      const entry = stack.find((e) => e.id === idRef.current);
      if (entry) entry.close = () => closeRef.current();
    }
  }, [onClose]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    ensureListener();

    if (open && !ownsEntryRef.current) {
      const id = Math.random().toString(36).slice(2);
      idRef.current = id;
      ownsEntryRef.current = true;
      stack.push({ id, close: () => closeRef.current() });
      try {
        window.history.pushState({ ...window.history.state, [SENTINEL_KEY]: id }, "");
      } catch {
        // pushState can throw in cross-origin sandboxes; harmless to ignore.
      }
    } else if (!open && ownsEntryRef.current) {
      const id = idRef.current;
      const idx = stack.findIndex((e) => e.id === id);
      if (idx >= 0) stack.splice(idx, 1);
      ownsEntryRef.current = false;
      idRef.current = null;

      // If the close was user-driven (X / ESC / outside-click), pop our
      // sentinel so the user doesn't have to press back twice.
      const state = window.history.state as { [k: string]: unknown } | null;
      if (!poppingFromUs && state && state[SENTINEL_KEY] === id) {
        try {
          popOwnedHistory();
        } catch {
          // ignore
        }
      }
    }
  }, [open]);

  // Cleanup on unmount.
  useEffect(() => {
    // StrictMode replays setup/cleanup without actually leaving the window.
    if (unmountTimer.current !== null) clearTimeout(unmountTimer.current);
    return () => {
      unmountTimer.current = setTimeout(() => {
      if (!ownsEntryRef.current) return;
      const id = idRef.current;
      const idx = stack.findIndex((e) => e.id === id);
      if (idx >= 0) stack.splice(idx, 1);
      ownsEntryRef.current = false;
      idRef.current = null;
      if (typeof window === "undefined") return;
      const state = window.history.state as { [k: string]: unknown } | null;
      if (!poppingFromUs && state && state[SENTINEL_KEY] === id) {
        try {
          popOwnedHistory();
        } catch {
          // ignore
        }
      }
      }, 0);
    };
  }, []);
}
